import React, { useState } from 'react';
import dayjs from 'dayjs';
import localeData from 'dayjs/plugin/localeData';
import weekday from 'dayjs/plugin/weekday';
import utc from 'dayjs/plugin/utc';
import 'dayjs/locale/ja';
import { useSelector, useDispatch } from 'react-redux';
import { useErrorHandler } from 'react-error-boundary';

import selectTime from '../../api/reserve/selectTime';
import { useDidMount, useNavigateWithId } from '../../customHooks';
import { saveTimearea, clearTime } from '../../redux_toolkit/reserveSlice';
import TopicPath from '../../components/TopicPath';
import SelectionSummary from '../../components/SelectionSummary';
import BackButton from '../../components/BackButton';
import configMastaApi from '../../api/masta/configMasta';
import MoveTop from '../../CommonParts';
import { CANCEL_ANNOUNCE } from '../../constants';

dayjs.extend(utc);
dayjs.extend(localeData);
dayjs.extend(weekday);
dayjs.locale('ja');

const SelectTime = function () {
  const navigate = useNavigateWithId();
  // const location = useLocation();
  const dispatch = useDispatch();
  const handleError = useErrorHandler();

  const lgCd = useSelector((state) => state.reserve.lgCd);
  const rsvSetId = useSelector((state) => state.reserve.rsvSetId);
  const rsvConfig = useSelector((state) => state.rsvConfig);
  const { topicPath = [] } = rsvConfig;

  // const state = location?.state ?? '';
  // const rsvDay = state.rsvDay ?? '';
  // const dayOfWeek = state.dayOfWeek ?? '';
  const rsvDay = useSelector((state) => state.reserve.rsvDate);
  const dayOfWeek = useSelector((state) => state.reserve.rsvDayOfWeek);

  const [dayInfo, setDayInfo] = useState({});
  const [rsvInterval, setRsvInterval] = useState('');
  const [taWidth, setTaWidth] = useState('23%');
  const [taColumns, setTaColumns] = useState(4);
  const [pageName, setPageName] = useState('');
  const [backUrl, setBackUrl] = useState('');
  const [remainDisplay, setRemainDisplay] = useState(false);

  // パンくずリスト設定から、ページ名・前の画面のURLをstateに設定
  const getBackUrlAndPageName = (topicPathConf) => {
    const locationPath = window.location.pathname;
    const tmpPath1 = locationPath.replace('/reserve/', '');
    const tmpPath2 = tmpPath1.replace('/test', '');
    const currentPath = tmpPath2.replace('/demo', '');

    let backUrl = '';
    let scnName = '';
    for (let i = 0; i < topicPathConf.length; i += 1) {
      const { url = '', scnName: tmpScnName = '' } = topicPathConf[i];
      if (currentPath === url) {
        // 「前に戻る」先のURL取得
        const { url: tmpBackUrl = '' } = topicPathConf[i - 1];
        if (tmpBackUrl === '/:lgCd') {
          backUrl = lgCd;
        } else {
          backUrl = tmpBackUrl;
        }

        // pageNameを取得
        scnName = tmpScnName;

        break;
      }
    }

    setPageName(scnName);
    setBackUrl(backUrl);
  };

  const init = async () => {
    // ページ名/戻る先URLの取得
    getBackUrlAndPageName(topicPath);

    // 選択した日の予約時間情報を取得
    const startDay = rsvDay;
    const {
      dayInfoList = {},
      rsvInterval = '',
      remainDisplay = false,
    } = await selectTime.getDayInfo(lgCd, startDay, rsvSetId).catch((err) => {
      window.alert('予約時間の取得に失敗しました。');
      handleError(err);
      return '';
    });

    // タイムエリアの幅設定取得
    const widthConf = await configMastaApi
      .findByConfCd(`timearea_width_${rsvInterval}`)
      .catch((err) => {
        window.alert('時間設定の取得に失敗しました。');
        handleError(err);
        return '';
      });

    const { value: widthVal = '23%' } = widthConf;

    // タイムエリアの列数設定取得
    const columnsConf = await configMastaApi
      .findByConfCd(`timearea_culumns_${rsvInterval}`)
      .catch((err) => {
        window.alert('時間設定の取得に失敗しました。');
        handleError(err);
        return '';
      });
    const { value: columnsVal = 4 } = columnsConf;

    const columnsNum = parseInt(columnsVal, 10);

    setDayInfo(dayInfoList);
    setRsvInterval(rsvInterval);
    setTaWidth(widthVal);
    setTaColumns(columnsNum);
    setRemainDisplay(remainDisplay);
  };

  useDidMount(() => {
    // この画面に入った時点で前回の時間選択をリセットする
    // → SelectionSummary の「利用時間」欄が非表示になる（「利用日」は残る）
    dispatch(clearTime());
    init();
  });

  const moveToReserve = (timearea) => {
    // ここでdate型文字列を作る
    const rsvDateWithTime = `${rsvDay} ${timearea.slice(0, timearea.length - 2)}:${timearea.slice(
      -2
    )}`;
    dispatch(
      saveTimearea({
        timearea,
        rsvDate: rsvDay,
        rsvDayOfWeek: dayOfWeek,
        rsvFlameId: 0,
        rsvDateWithTime,
      })
    );
    // window.alert(`予約画面に遷移します。`);
    navigate('/userinfo');
  };

  const createTimeSelector = (dayInfo) => {
    const keys1 = Object.keys(dayInfo);
    const timeSelector = [];

    for (let h = 0; h < keys1.length; h += 1) {
      const currentKey1 = keys1[h];
      const currentInfo = dayInfo[currentKey1];
      const keys2 = Object.keys(currentInfo);
      if (keys2.length !== 0) {
        const firsttime = currentInfo[keys2[0]]?.display;
        const firstMinutes = firsttime.substring(firsttime.length - 2);
        if (firstMinutes !== '00') {
          const firstMinutesNum = parseInt(firstMinutes, 10);
          const intervalNum = parseInt(rsvInterval, 10);
          const blankNum = firstMinutesNum / intervalNum;
          for (let blankidx = 0; blankidx < parseInt(blankNum, 10); blankidx += 1) {
            timeSelector.push(<li className="notset" style={{ width: taWidth }} />);
          }
        }
      }

      for (let i = 0; i < keys2.length; i += 1) {
        const currentKey2 = keys2[i];
        const { display = '', enable = false, rsvStat = '', className } = currentInfo[currentKey2];

        if (enable) {
          timeSelector.push(
            <li className={className} style={{ width: taWidth }}>
              <a
                role="button"
                // href="javascript:void(0);"
                onClick={() => moveToReserve(currentKey2)}
                tabIndex={0}
              >
                {display}
                <span>{rsvStat}</span>
              </a>
            </li>
          );
        } else {
          timeSelector.push(
            <li className={className} style={{ width: taWidth }}>
              {display}
              <span>{rsvStat}</span>
            </li>
          );
        }
      }
    }

    // if (innerWidth >= 481) {
    // while (timeSelector.length % taColumns !== 0) {
    // ゼロ除算はNaNになるため taColumnsが0の場合は実施しない
    while (taColumns !== 0 && timeSelector.length % taColumns !== 0) {
      timeSelector.push(<li className="notset" style={{ width: taWidth }} />);
    }
    // }

    return timeSelector;
  };

  // 空き状況の説明部分を作成
  const createRemainExplain = () => {
    if (remainDisplay) {
      return (
        <p id="reserve_text">
          <span className="possible">◯</span> 予約可能　<span className="few">△</span> 残りわずか
          <span className="reserved">×</span> 予約済み
          <span className="notset">&nbsp;&nbsp;&nbsp;&nbsp;</span> 予約不可
        </p>
      );
    }
    return (
      <p id="reserve_text">
        <span className="possible">◯</span> 予約可能　<span className="reserved">×</span> 予約済み
        <span className="notset">&nbsp;&nbsp;&nbsp;&nbsp;</span> 予約不可
      </p>
    );
  };

  return (
    <>
      <TopicPath />
      <SelectionSummary showNote />
      <main>
        <article>
          <div id="contents">
            <MoveTop lgCd={lgCd} />
            <div className="inner630">
              <h2>{pageName}</h2>
              <p className="point">{dayjs(rsvDay).format('YYYY[年]M[月]D[日](ddd)')}</p>
              <p className="txtCenter">
                利用時間を選択してください。
                <br />
                <span className="fs_14px">
                  ※予約の残り枠数は変動しますので、希望の日時に予約いただけない場合がございます。
                </span>
              </p>
              <div id="timeBox">
                <ul id="selectTime" className="row jcSB">
                  {createTimeSelector(dayInfo)}
                </ul>
                {createRemainExplain()}
              </div>
              <p id="navi" className="row jcC">
                <BackButton url={`/${backUrl}`} />
              </p>
            </div>
            <div className="cancelAnnounce">{CANCEL_ANNOUNCE}</div>
          </div>
        </article>
      </main>
    </>
  );
};

export default SelectTime;
